import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Hidden,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import IconSearch from '@eduzz/houston-icons/SearchOutline';
import { enStatus, IModule, blockedStatus } from 'interface/course';
import { useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronUp } from '@eduzz/houston-icons';
import LockIcon from '@eduzz/houston-icons/LockedSolid';
import nothingToShowImage from 'assets/img/nocomments.png';
import { dateFormater } from 'utils';
import SkeletonModules from './Skeleton';
import Lessons from './Lessons';
import { CourseTheme } from 'assets/themes/Course';
import { useAppSelector } from 'hooks/redux';
import { clearModules, getModules } from 'redux/modules/slice';
import SearchLessons from './SearchLessons';
import IconCancel from '@eduzz/houston-icons/Cancel';
import { useGlobalSearch } from 'hooks/globalSearch';

const useStyles = makeStyles<CourseTheme>((theme) => ({
  container: {
    width: '100%',
    '& h3, p': {
      color: theme.variables.courseTheme.text,
      whiteSpace: 'normal',
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': 4,
      '-webkit-box-orient': 'vertical',
      wordBreak: 'break-word',
    },
    '& > div:before': {
      content: 'none',
    },
    '& span > h3': {
      fontWeight: 600,
      color: theme.variables.courseTheme.text,
    },
  },
  searchContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& > div': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  inputContainer: {
    marginRight: theme.spacing(4),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    background: theme.variables.courseTheme.cardBackground,
    borderRadius: theme.shape.borderRadius,
    height: 35,
    width: 94,
    transition: '0.3s ease',
    '&:hover': {
      width: 320,
    },
    '& > div:last-child': {
      width: '100%',
    },
    '& svg': {
      fill: theme.variables.courseTheme.text,
      marginBottom: theme.spacing(-1),
    },
    '& input': {
      background: theme.variables.courseTheme.cardBackground,
      color: theme.variables.courseTheme.text,
      borderWidth: '0px',
      paddingLeft: '0px !important',
      margin: '0px !important',
      height: '35px !important',
      shadowBox: 'none',
      '&::placeholder': {
        opacity: 0.55,
      },
    },
  },
  accordion: {
    background: theme.variables.courseTheme.cardBackground,
    borderWidth: '0px',
    boxShadow: 'none',
    marginTop: theme.spacing(6),
    borderRadius: 8,
  },
  summary: {
    borderRadius: 8,
    '& > div, > span': {
      display: 'flex',
      flexDirection: 'column',
      whiteSpace: 'nowrap',
      '@media(max-width: 768px)': {
        paddingLeft: 0,
        paddingRight: 0,
        width: '100%',
      },
    },
    padding: theme.spacing(3, 7),
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
  },
  expandIcon: {
    transform: 'none !important',
    padding: 0,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
  },
  accordionIndicator: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: theme.spacing(10),
    justifyContent: 'flex-end',
    '& p': {
      fontWeight: 600,
      color: theme.variables.courseTheme.text,
    },
    '& > div': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'baseline',
    },
    '& div, & svg': {
      transition: '300ms',
      color: theme.variables.courseTheme.text,
    },
    '& > div:last-child svg': {
      color: theme.palette.primary.main,
    },
  },
  accordionDetails: {
    background: theme.palette.safevideo.main,
    padding: 0,
    margin: 0,
  },
  emptyContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 0),
    background: theme.variables.courseTheme.background,
    opacity: 0.8,
    '& img': {
      marginBottom: theme.spacing(4),
      '@media(max-width: 425px)': {
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(2),
      },
    },
    '& h3, p': {
      textAlign: 'center',
      marginTop: theme.spacing(4),
      color: theme.variables.courseTheme.text,
    },
  },
}));

const Modules = () => {
  const [currentAccordion, setCurrentAccordion] = useState(null);
  const classes = useStyles();
  const modules = useAppSelector((state) => state.newModules.modules);
  const { courseHash } = useParams<{ courseHash: string }>();
  const dispatch = useDispatch();
  const disableHeaderSearch = useMediaQuery('@media(min-width: 600px)');
  const { searchText, onSearchChange } = useGlobalSearch({
    disabled: disableHeaderSearch,
    placeholder: 'Buscar por aula',
  });
  const searchRef = useRef(null);

  const renderModuleStatusIndicator = (module: IModule) => {
    if (!blockedStatus.includes(module.status)) {
      return null;
    }
    const messageMapper = {
      [enStatus.BLOCKED]: 'bloqueado',
      [enStatus.EXPIRED]: 'expirado',
      [enStatus.NOT_RELEASED]: 'não lançado',
      [enStatus.MUST_ACCEPT_TERM]: 'É necessário aceitar os termos',
    };
    const tooltipMapper = {
      [enStatus.BLOCKED]: 'Você ainda não possui acesso a esse módulo',
      [enStatus.EXPIRED]: 'Esse módulo está expirado',
      [enStatus.MUST_ACCEPT_TERM]: 'É necessário aceitar os termos',
      [enStatus.NOT_RELEASED]: module.release
        ? `Esse módulo será liberado à partir do dia ${dateFormater(module.release)}`
        : 'Esse módulo está bloqueado, aguarde a liberação',
    };
    return (
      <Box>
        <Typography aria-label='Status do módulo'>
          {module.status !== enStatus.MUST_ACCEPT_TERM ? 'Módulo' : ''} {messageMapper[module.status]}
        </Typography>
        <Box marginLeft={2}>
          <Tooltip title={tooltipMapper[module.status]}>
            <LockIcon />
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const renderAccordionIndicator = (module: IModule) => {
    const isOpened = currentAccordion === module.id;
    return (
      <div className={classes.accordionIndicator}>
        {renderModuleStatusIndicator(module)}
        <div>
          <Box marginRight={2}>
            {!isOpened && <Typography>Abrir</Typography>}
            {isOpened && <Typography>Ocultar</Typography>}
          </Box>
          <div style={{ transform: `rotate(${isOpened ? 0 : 180}deg)` }}>
            <ChevronUp />
          </div>
        </div>
      </div>
    );
  };

  const onExpand = (id: number) => () => {
    setCurrentAccordion((currentId: number) => {
      if (currentId == id) return null;
      return id;
    });
  };

  useEffect(() => {
      dispatch(getModules(courseHash));
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearModules());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!searchText?.length) {
      return;
    }
    const element = document.getElementById('modules-list');
    if (!element) {
      return;
    }
    const yOffset = -100;
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }, [searchText]);

  if (!modules) {
    return <SkeletonModules />;
  }

  return (
    <div aria-label='Lista de módulos do curso' id='modules-list' className={classes.container}>
      <Box marginBottom={4} className={classes.searchContainer}>
        <Typography variant='h3'>{searchText?.length ? 'Aulas' : 'Módulos'}</Typography>
        <Hidden xsDown>
          <div
            aria-label='Campo de pesquisa de aulas'
            id='lessons-search-input'
            style={{ width: searchText?.length > 0 ? 320 : undefined }}
            className={`${classes.inputContainer} old-input`}
          >
            <Box margin={2}>
              <IconSearch />
            </Box>
            <TextField
              inputRef={searchRef}
              placeholder='Buscar Aula'
              className={classes.input}
              onChange={onSearchChange}
              InputProps={{
                disableUnderline: true,
                endAdornment: searchText?.length > 0 && (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='Limpar pesquisa'
                      onClick={() => {
                        searchRef.current.value = '';
                        onSearchChange('');
                      }}
                      edge='end'
                    >
                      <IconCancel />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </Hidden>
      </Box>
      <SearchLessons />
      {!searchText?.length &&
        Array.isArray(modules) &&
        modules.map((module) => (
          <Accordion key={module.id} classes={{ root: classes.accordion }} expanded={currentAccordion === module.id}>
            <AccordionSummary
              IconButtonProps={{ disableFocusRipple: true, disableRipple: true }}
              onClick={onExpand(module.id)}
              expandIcon={renderAccordionIndicator(module)}
              classes={{ expandIcon: classes.expandIcon, root: classes.summary }}
            >
              <div>
                <Typography aria-label='Título do módulo' variant='h3' id='course-modules-module-title'>
                  {module.title}
                </Typography>
                <Box marginTop={2}>
                  <Typography aria-label='Total de aulas do módulo' variant='body1'>
                    {module.total_lessons ?? 0} aula(s)
                  </Typography>
                </Box>
              </div>
            </AccordionSummary>
            <AccordionDetails classes={{ root: classes.accordionDetails }}>
              {Boolean(module.total_lessons) && (
                <Lessons lessons={module.lessons} blockedModule={blockedStatus.includes(module.status)} />
              )}
              {!Boolean(module.total_lessons) && (
                <div aria-label='Campo informativo de módulo vazio' className={classes.emptyContainer}>
                  <img src={nothingToShowImage} alt='módulo vazio' />
                  <Typography variant='h3'>Suas aulas aparecerão aqui...</Typography>
                  <Typography variant='body1'>Aqui você poderá assistir e consultar todas as suas aulas.</Typography>
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
    </div>
  );
};

export default Modules;
